/* eslint-disable react/display-name, react/prop-types */

import React, { useState, useEffect, useRef, useCallback } from 'react';

import AuthenticatedContainer from 'components/AuthenticatedContainer';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TermsOfUseContent from '../Components/TermsOfUseContent';
import {
  NAV_HEIGHT,
  PAGE_TOP_PADDING,
  applyPageVisibility,
  buildPages,
  getBlockElements,
} from '../Components/paginatePolicy';

export const TermsOfUse = () => {
  const outerRef = useRef(null);
  const measureRef = useRef(null);
  const displayRef = useRef(null);
  const pagesRef = useRef([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);

  const computePages = useCallback(() => {
    const outer = outerRef.current;
    const measureEl = measureRef.current;
    if (!outer || !measureEl) return;

    const availableHeight = outer.clientHeight - NAV_HEIGHT - PAGE_TOP_PADDING;
    if (availableHeight <= 0) return;

    const policyRoot = measureEl.querySelector('.policy');
    if (!policyRoot) return;

    const blocks = getBlockElements(policyRoot);
    if (blocks.length === 0) return;

    const newPages = buildPages(blocks, availableHeight);

    pagesRef.current = newPages;
    setTotalPages(newPages.length);
    setCurrentPage(prev => Math.min(prev, Math.max(0, newPages.length - 1)));
  }, []);

  useEffect(() => {
    if (displayRef.current && pagesRef.current.length > 0) {
      applyPageVisibility(displayRef.current, pagesRef.current[currentPage] || []);
      const needsScroll = displayRef.current.scrollHeight > displayRef.current.clientHeight;
      displayRef.current.style.overflowY = needsScroll ? 'auto' : 'hidden';
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    const timer = setTimeout(computePages, 100);
    const observer = new ResizeObserver(computePages);
    if (outerRef.current) observer.observe(outerRef.current);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [computePages]);

  const isLastPage = totalPages > 0 && currentPage === totalPages - 1;

  return (
    <AuthenticatedContainer>
      <Box
        ref={outerRef}
        sx={{
          width: '100%',
          height: 'calc(100dvh - 140px)',
          maxHeight: 'calc(100dvh - 140px)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Hidden measurement container */}
        <Box
          ref={measureRef}
          sx={{
            position: 'absolute',
            visibility: 'hidden',
            top: 0,
            left: 0,
            right: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: -1,
          }}
        >
          <TermsOfUseContent />
        </Box>

        {/* Visible display */}
        <Box ref={displayRef} sx={{ flex: 1, overflow: 'hidden', pt: `${PAGE_TOP_PADDING}px` }}>
          <TermsOfUseContent />
        </Box>

        {/* Navigation bar */}
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: '1px solid #e0e0e0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minHeight: 40,
            }}
          >
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
              {currentPage > 0 && (
                <Button onClick={() => setCurrentPage(p => p - 1)}>
                  Back
                </Button>
              )}
            </Box>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              {totalPages > 1 && (
                <Typography variant="body2" color="text.secondary">
                  Page {currentPage + 1} of {totalPages}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              {!isLastPage && (
                <Button onClick={() => setCurrentPage(p => p + 1)}>
                  {'Next ->'}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </AuthenticatedContainer>
  );
};

export default TermsOfUse;
